import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Tag from "@/components/base/Tag";
import { factChecks, factVerdicts } from "@/mocks/factChecks";
import { cases } from "@/mocks/cases";
import { siteInfo } from "@/mocks/site";

export default function FactCheckPage() {
  const [active, setActive] = useState<string>("全部");

  const list = useMemo(
    () => (active === "全部" ? factChecks : factChecks.filter((item) => item.verdict === active)),
    [active],
  );

  const counts = useMemo(
    () => ({
      谣言: factChecks.filter((item) => item.verdict === "谣言").length,
      属实: factChecks.filter((item) => item.verdict === "属实").length,
      存疑: factChecks.filter((item) => item.verdict === "存疑").length,
    }),
    [],
  );

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "真相核验 · 辟谣速查",
      description:
        "汇总已核验信息的真假结论，按谣言、属实、存疑分类，标注核验依据与对应事件。",
      url: `${siteInfo.url}/fact-check`,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: factChecks.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
        })),
      },
    },
  ];

  return (
    <>
      <Seo
        title="真相核验与辟谣速查｜社会爆料真假结论 - 棱镜求证"
        description="按谣言、属实、存疑分类查看社会爆料核验结论，每条结论均标注核验依据、发布时间与对应事件，帮助快速判断信息真假。"
        keywords="社会爆料辟谣,社会爆料真假,真相核验,辟谣速查,求证"
        path="/fact-check"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "真相核验", path: "/fact-check" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            真相核验 · 辟谣速查
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里收录已经形成初步结论的信息核验。我们不会只给一个“真”或“假”，
            而是把核验依据一并列出，方便你自行复核。
          </p>

          <div className="mt-7 grid grid-cols-3 gap-4 sm:max-w-lg">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-label text-2xl font-semibold text-primary-600">{counts.谣言}</p>
              <p className="mt-1 text-xs text-foreground-500">判定为谣言</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-label text-2xl font-semibold text-secondary-600">{counts.属实}</p>
              <p className="mt-1 text-xs text-foreground-500">核实为属实</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="font-label text-2xl font-semibold text-foreground-700">{counts.存疑}</p>
              <p className="mt-1 text-xs text-foreground-500">证据存疑</p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
          <div className="mb-6 inline-flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {factVerdicts.map((verdict) => (
              <button
                key={verdict}
                type="button"
                onClick={() => setActive(verdict)}
                className={`cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === verdict
                    ? "bg-primary-500 text-white"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {verdict}
              </button>
            ))}
          </div>

          <ul className="space-y-4">
            {list.map((item) => {
              const relatedCase = cases.find((c) => c.id === item.caseId);
              return (
                <li
                  key={item.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Tag tone={item.verdict === "谣言" ? "primary" : item.verdict === "属实" ? "secondary" : "neutral"}>
                          {item.verdict === "谣言" ? "判定为谣言" : item.verdict === "属实" ? "核实为属实" : "证据存疑"}
                        </Tag>
                        <Tag tone="outline">{item.category}</Tag>
                        <span className="text-xs text-foreground-500">{item.date}</span>
                      </div>
                      <h2 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                        {item.title}
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

                      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                        <span className="flex items-center gap-1.5">
                          <i className="ri-file-search-line text-sm" aria-hidden="true" />
                          核验依据：{item.basis}
                        </span>
                      </div>
                    </div>

                    {relatedCase ? (
                      <div className="shrink-0 md:w-52">
                        <Link
                          to={`/cases/${relatedCase.slug}`}
                          className="group block rounded-md border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-300"
                        >
                          <span className="text-xs text-foreground-500">对应事件</span>
                          <p className="mt-1 line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                            {relatedCase.title}
                          </p>
                          <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                            查看时间线
                            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
                          </span>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  还想进一步了解怎么判断？
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  求证指南把核验方法拆成了可以直接照做的步骤，从找源头到避免二次传播，都有对应说明。
                </p>
              </div>
              <Link
                to="/guide"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-foreground-950 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-book-open-line text-base" aria-hidden="true" />
                <span className="whitespace-nowrap">阅读求证指南</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}